import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the CampaignsContext
const CampaignsContext = createContext();

// Custom hook to use the CampaignsContext
export const useCampaigns = () => useContext(CampaignsContext);

// Provider component to wrap the app
export const CampaignsProvider = ({ children }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const API_URL = "https://hrmspayroll.prepstripe.com/api/campaigns"; // Replace with your actual API URL
  const CHECKLIST_URL = "https://hrmspayroll.prepstripe.com/api/checklists";
  const ASSESSMENT_URL = "https://hrmspayroll.prepstripe.com/api/technical-assessments";
  const token = getToken();
  const [loading, setLoading] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(null); // id of campaign being updated or null

  // Fetch campaigns from the API on load
  const fetchCampaigns = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      await new Promise((r) => setTimeout(r, 1000));
      setLoading(false);
      // setCampaigns(response.data?.data?.data || []); // Access `data.data` for actual campaigns
      setCampaigns(response.data?.data?.data.slice().reverse());
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [API_URL, token]);

  // Add a new campaign
  // const addCampaign = async (newCampaign) => {
  //   try {
  //     const response = await axios.post(API_URL, newCampaign, {
  //       headers: {
  //         Authorization: `Bearer ${getToken()}`,
  //       },
  //     });
  //     setCampaigns((prev) => [...prev, response.data?.data]);
  //     return response.data?.data
  //   } catch (error) {
  //     console.error("Error adding campaign:", error);
  //   }
  // };

  const addCampaign = async (newCampaign) => {
    try {
      const response = await axios.post(API_URL, newCampaign, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setCampaigns((prev) => [...prev, response.data?.data]);
      setCampaigns((prev) => [response.data?.data, ...prev]);

      return response.data?.data; // ✅ Always return a value
    } catch (error) {
      console.error("Error adding campaign:", error);

      // ✅ Check if the error has a response and return the message
      if (error.response) {
        return error.response.data; // This contains { success: false, message: "This form has already been assigned to an existing campaign." }
      }

      return null; // Return null if there's no error response
    }
  };

  // Update an existing campaign
  const updateCampaign = async (updatedCampaign) => {
    setCampaignLoading(updatedCampaign.id);
    try {
      const response = await axios.put(
        `${API_URL}/${updatedCampaign.id}`,
        updatedCampaign,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === updatedCampaign.id
            ? { ...campaign, ...response.data?.data }
            : campaign
        )
      );
      return response?.data;
    } catch (error) {
      console.error("Error updating campaign:", error);
    } finally {
      setCampaignLoading(null);
    }
  };

  // Delete a campaign
  const deleteCampaign = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setCampaigns((prev) => prev.filter((campaign) => campaign.id !== id));
    } catch (error) {
      console.error("Error deleting campaign:", error);
    }
  };

  // Update checklist for a specific campaign
  const updateChecklist = async (campaignId, checklistItems) => {
    try {
      // Check if there is only one checklist item
      let payload;
      if (checklistItems.length === 1) {
        payload = {
          campaign_id: campaignId,
          name: checklistItems[0].name,
        };
      } else {
        // Otherwise, send as an array (if needed for multiple)
        payload = checklistItems.map((item) => ({
          campaign_id: campaignId,
          name: item.name,
        }));
      }

      // console.log("payload: ", payload)

      // Make the POST request to update the checklist
      const response = await axios.post(CHECKLIST_URL, payload, {
        headers: {
          Authorization: `Bearer ${getToken()}`, // Include authorization token
        },
      });

      // console.log(campaigns)
      // console.log("Checklist updated successfully:", response.data);
      return response.data;
    } catch (error) {
      // Handle errors appropriately
      console.error(
        "Error updating checklist:",
        error.response?.data || error.message
      );
      throw error; // Re-throw error for calling function to handle
    }
  };

  // Update technical assessments for a specific campaign
  const updateAssessments = async (campaignId, assessments) => {
    try {
      let payload;

      // Check if there's only one assessment
      if (assessments.length === 1) {
        payload = {
          campaign_id: campaignId,
          name: assessments[0].name,
          link: assessments[0].link,
        };
      } else {
        // Otherwise, format as an array for multiple assessments
        payload = assessments.map((assessment) => ({
          campaign_id: campaignId,
          name: assessment.name,
          link: assessment.link,
        }));
      }

      // Make the POST request to update the assessments
      const response = await axios.post(ASSESSMENT_URL, payload, {
        headers: {
          Authorization: `Bearer ${getToken()}`, // Include authorization token
        },
      });

      // Log the response or handle as needed
      // console.log("Technical assessments updated successfully:", response.data);
      return response.data;
    } catch (error) {
      // Handle errors appropriately
      console.error(
        "Error updating technical assessments:",
        error.response?.data || error.message
      );
      throw error; // Re-throw error for calling function to handle
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      loading,
      campaigns,
      pagination,
      fetchCampaigns,
      addCampaign,
      updateCampaign,
      deleteCampaign,
      updateChecklist,
      updateAssessments,
      campaignLoading,
      setCampaignLoading,
    }),
    [campaigns, loading, campaignLoading] // Recalculate the value only when `campaigns` or loading changes
  );

  return (
    <CampaignsContext.Provider value={value}>
      {children}
    </CampaignsContext.Provider>
  );
};
